/**
 * Citation source-tier styling (source-attribution feature). ONE source of
 * truth for the Library/AI/Web tier colours + labels in the chat UI. Tailwind
 * classes are LITERAL strings (no template interpolation) so the JIT compiler
 * always emits them.
 */

import type { Citation } from './chat-types';

export type CitationTier = 'library' | 'ai' | 'web';

export interface TierStyle {
  id: CitationTier;
  label: string;
  /** Verified = faithfulness-checked customer knowledge (library only). */
  verified: boolean;
  /** Legend / tooltip copy. */
  hint: string;
  /** Icon text colour. */
  icon: string;
  /** Small tier badge (outline) text + border colour. */
  badge: string;
  /** Legend dot background. */
  dot: string;
  /** Left-accent border on the source card. */
  accent: string;
  /** Filled inline [N] marker badge (bg + text + hover). */
  inlineBadge: string;
}

export const CITATION_TIERS: Record<CitationTier, TierStyle> = {
  library: {
    id: 'library',
    label: 'Library',
    verified: true,
    hint: 'Your documents, wiki, connectors & MCP servers — verified',
    icon: 'text-blue-500 dark:text-blue-400',
    badge: 'text-blue-600 border-blue-300 dark:text-blue-300 dark:border-blue-700',
    dot: 'bg-blue-500',
    accent: 'border-l-2 border-l-blue-400',
    inlineBadge: 'bg-blue-500/10 text-blue-600 hover:bg-blue-500/20 dark:text-blue-300',
  },
  ai: {
    id: 'ai',
    label: 'AI',
    verified: false,
    hint: "The model's own knowledge — unverified",
    icon: 'text-amber-500 dark:text-amber-400',
    badge: 'text-amber-600 border-amber-300 dark:text-amber-300 dark:border-amber-700',
    dot: 'bg-amber-500',
    accent: 'border-l-2 border-l-amber-400',
    inlineBadge: 'bg-amber-500/10 text-amber-600 hover:bg-amber-500/20 dark:text-amber-300',
  },
  web: {
    id: 'web',
    label: 'Web',
    verified: false,
    hint: 'Web search result — external, unverified',
    icon: 'text-teal-500 dark:text-teal-400',
    badge: 'text-teal-600 border-teal-300 dark:text-teal-300 dark:border-teal-700',
    dot: 'bg-teal-500',
    accent: 'border-l-2 border-l-teal-400',
    inlineBadge: 'bg-teal-500/10 text-teal-600 hover:bg-teal-500/20 dark:text-teal-300',
  },
};

/** Resolve a citation's tier, defaulting legacy citations to 'library'. */
export function tierOf(c: Pick<Citation, 'sourceType'>): CitationTier {
  const t = c.sourceType;
  return t === 'ai' || t === 'web' ? t : 'library';
}

export function tierStyle(tier: CitationTier): TierStyle {
  return CITATION_TIERS[tier];
}

/** The ordered list of tiers actually present in a citation set (for the legend). */
export function presentTiers(citations: Array<Pick<Citation, 'sourceType'>>): CitationTier[] {
  const present = new Set(citations.map(tierOf));
  return (['library', 'ai', 'web'] as const).filter((t) => present.has(t));
}
